<template>
  <Card.Base>
    <Card.Title :icon="file ? InfoIcon : undefined">
      <div v-if="file">Copyright info</div>
      <Skeleton v-else class="w-[170px] h-4" />
    </Card.Title>
    <Card.Content>
      <template v-if="file">
        <!-- File name -->
        <div class="mb-5">
          <div
            class="font-medium text-[13px] leading-[18px] text-gray-800 mb-1"
          >
            File
          </div>
          <div class="leading-[18px] text-gray-800 [word-break:break-word]">
            {{ file.metadata.originalFilename }}
          </div>
        </div>

        <div class="flex flex-col tablet-h:flex-row">
          <!-- Left column -->
          <div class="w-full desktop:w-[55%] mr-10">
            <!-- Owners -->
            <EntitySection
              :label="pluralize('Owner', owners.length)"
              :entities="owners"
              :is-owner="isOwner"
              :tooltip="'Owners of this copyright which may be an individual or organization'"
              add-label="Add co-owner"
              @info-click="handleOwnerInfoClick"
              @add-click="handleAddCoOwnerClick"
              @more-click="handleAllOwnersClick"
            />

            <!-- Authors -->
            <EntitySection
              :label="pluralize('Author', authors.length)"
              :entities="authors"
              :is-owner="isOwner"
              :tooltip="'Creator(s) of the work who may or may not be an owner of the work'"
              add-label="Add author"
              :show-empty="true"
              @info-click="handleAuthorInfoClick"
              @add-click="handleAddAuthorClick"
              @more-click="handleAllAuthorsClick"
            />

            <!-- Registrant -->
            <div class="mb-5">
              <div
                class="font-medium text-[13px] leading-[18px] text-gray-800 mb-1"
              >
                Registrant
              </div>
              <div class="leading-[18px] text-gray-800 break-all">
                {{ file.registrant.name }}
              </div>
            </div>

            <!-- Dacr Token ID -->
            <div class="mb-5">
              <div
                class="font-medium text-[13px] leading-[18px] text-gray-800 mb-1"
              >
                Dacr Token ID
              </div>
              <div class="leading-[18px] text-gray-800 break-all">
                {{ file.copyright.id }}
              </div>
            </div>
          </div>

          <!-- Right column -->
          <div class="w-full desktop:w-[45%]">
            <!-- Dacr Timestamp / Registrations -->
            <div class="mb-5">
              <div
                class="font-medium text-[13px] leading-[18px] text-gray-800 mb-1 flex items-center"
              >
                <div>Dacr Timestamp</div>
              </div>
              <div
                v-for="registration in file.copyright.registrations"
                :key="registration.origin.id"
                class="mb-2 last:mb-0 flex items-center"
              >
                <Avatar
                  v-if="registration.origin.flag.url"
                  :size="14"
                  :src="registration.origin.flag.url"
                  class="mr-1.5"
                />
                <DacrMarkIcon v-else class="size-[14px] text-gray-800 mr-1.5" />

                <div
                  class="leading-[18px] text-gray-800 flex items-center mr-2"
                >
                  {{ formatTimestamp(registration.timestamp) }}
                </div>

                <a
                  v-if="canViewCertificates && registration.certificateUrl"
                  :href="registration.certificateUrl"
                  aria-label="View certificate"
                  @click="
                    openCertificateViewer(registration.certificateUrl, $event)
                  "
                  target="_blank"
                  class="relative group text-link text-[12px] leading-[12px] after:hidden hover:after:block after:rounded-md after:content-[''] after:w-[calc(100%+12px)] after:h-5 after:bg-green-200/25 after:absolute after:top-[-4px] after:-left-1.5"
                >
                  <div class="flex items-center relative z-10 gap-x-1">
                    <FileTextIcon class="size-[12px] text-current" />
                    <div
                      class="hidden group-hover:block desktop:group-hover:hidden min-[1330px]:group-hover:block"
                    >
                      View certificate
                    </div>
                    <ChevronRightIcon class="size-[10px] text-current" />
                  </div>
                </a>
              </div>
            </div>

            <!-- Type -->
            <div>
              <div
                class="font-medium text-[13px] leading-[18px] text-gray-800 mb-1"
              >
                Type
              </div>
              <div class="leading-[18px] text-gray-800 flex items-center">
                <div class="size-[18px] mr-2 shrink-0">
                  <Thumbnail :type="fileTypeToThumbnail(file.type)" />
                </div>
                <div>{{ friendlyFileType(file.type) }}</div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Loading skeleton -->
      <template v-else>
        <Skeleton class="w-10 h-2.5 mt-6 mb-3" />
        <Skeleton class="w-[118px] h-3 mb-7" />
        <Skeleton class="w-10 h-2.5 mb-3" />
        <Skeleton class="w-[118px] h-3 mb-7" />
        <Skeleton class="w-10 h-2.5 mb-3" />
        <Skeleton class="w-[250px] h-3" />
        <Skeleton class="w-[118px] h-3" />
      </template>
    </Card.Content>
  </Card.Base>
</template>

<script setup lang="ts">
import { openCertificateViewer } from '@/features/copyright/certificateViewer'
import { Avatar, Skeleton, Thumbnail } from '@dacr/library'
import {
  InfoIcon,
  DacrMarkIcon,
  FileTextIcon,
  ChevronRightIcon,
} from '@dacr/icons'
import { Card } from '@/shared/components/Card'
import { formatTimestamp } from '@/shared/utils/datetime'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { friendlyFileType, getEntityInfo } from '@/shared/utils/fileHelpers'
import { FileRole } from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { computed } from 'vue'
import { useDevice } from '@/shared/composables/useDevice'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import EntitySection from '@/shared/components/EntitySection.vue'
import pluralize from 'pluralize'

const props = defineProps<{
  file?: CopyrightFile
}>()

const device = useDevice()

const isOwner = computed(() => props.file?.role === FileRole.Owner)
const canViewCertificates = computed(() => props.file?.role !== FileRole.Public)

const owners = computed(() => {
  if (!props.file) return []

  const result: { id: string; name: string }[] = []

  const ownerInfo = props.file.owner
  if ('id' in ownerInfo) {
    result.push({ id: ownerInfo.id, name: ownerInfo.name })
  }

  for (const edge of props.file.coOwners.edges) {
    if (edge.node.__typename === 'EntityWithRole') {
      const info = getEntityInfo(edge.node.entity)
      result.push({ id: info.id, name: info.name })
    }
  }

  return result
})

const authors = computed(() => {
  if (!props.file) return []

  const result: { id: string; name: string }[] = []

  for (const edge of props.file.authors.edges) {
    if (edge.node.__typename === 'EntityWithRole') {
      const info = getEntityInfo(edge.node.entity)
      result.push({ id: info.id, name: info.name })
    }
  }

  return result
})

const handleAllOwnersClick = () => {
  openDialog(Dialog.AllOwners, {
    props: { file: props.file },
  })
}

const handleAllAuthorsClick = () => {
  openDialog(Dialog.AllAuthors, {
    props: { file: props.file },
  })
}

const handleAddCoOwnerClick = () => {
  openDialog(Dialog.FileOwners, {
    props: { file: props.file },
  })
}

const handleAddAuthorClick = () => {
  openDialog(Dialog.FileAuthors, {
    props: { file: props.file },
  })
}

const handleOwnerInfoClick = () => {
  if (!device.isMobile.value) return
  openDialog(Dialog.FileAccess, {
    props: {
      content:
        'Owners of this copyright which may be an individual or organization',
    },
  })
}

const handleAuthorInfoClick = () => {
  if (!device.isMobile.value) return
  openDialog(Dialog.FileAccess, {
    props: {
      content:
        'Creator(s) of the work who may or may not be an owner of the work',
    },
  })
}
</script>
